import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Save,
    AlertCircle,
    CheckCircle,
    Settings,
    Clock,
    Bell,
    Play,
    Loader2,
    Mail,
    Plus,
    X,
    Shield
} from 'lucide-react';
import { apiClient, LicenceCheckSettings, UpdateLicenceCheckSettingsRequest, TestSchedulerResponse } from '../services/api';
import { useScoringConfig } from '../contexts/ScoringConfigContext';

interface AutomationSettingsProps {
    user: any;
    onSettingsChange?: (settings: any) => void;
}

const AutomationSettings: React.FC<AutomationSettingsProps> = ({ user, onSettingsChange }) => {
    const location = useLocation();
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();

    // Check if this is the demo organization
    const isDemoOrg = user?.organisation?.id === '9435251e-0099-4ddf-acad-dc893186419c' || user?.organisation?.id === '2ad339da-8608-4936-a5e7-942b5cf67b9d' || user?.organisation?.id === '4afe1ce3-60d8-4ea5-b315-cf82e5be1b34';

    const [settings, setSettings] = useState<LicenceCheckSettings | null>(null);
    const [originalSettings, setOriginalSettings] = useState<LicenceCheckSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [highlightConsentSection, setHighlightConsentSection] = useState(false);

    useEffect(() => {
        if (location.hash === '#consent-check') {
            setTimeout(() => {
                setHighlightConsentSection(true);
                setTimeout(() => setHighlightConsentSection(false), 2500);
            }, 150);
        }
    }, [location.hash]);

    // Coverage summary state
    const [testResult, setTestResult] = useState<TestSchedulerResponse | null>(null);

    // Run now states
    const [isRunning, setIsRunning] = useState(false);

    // Email input state
    const [emailInput, setEmailInput] = useState('');

    // Save confirmation state
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingSettings, setPendingSettings] = useState<UpdateLicenceCheckSettingsRequest | null>(null);

    // Run now confirmation state
    const [showRunNowConfirmation, setShowRunNowConfirmation] = useState(false);

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSettings = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getAutomationSettings();
            // Ensure notificationEmailRecipients is always an array
            let loadedSettings = {
                ...response,
                notificationEmailRecipients: response.notificationEmailRecipients || []
            };

            // For demo org: Force checkboxes to appear checked for display purposes
            // This allows all fields to be visible even if disabled in database
            if (isDemoOrg) {
                loadedSettings = {
                    ...loadedSettings,
                    enabled: true,
                    autoCheckOnConsent: true
                };
            }

            setSettings(loadedSettings);
            setOriginalSettings(loadedSettings);
        } catch (err: any) {
            setError(err.message || 'Failed to load automation settings');
            console.error('Error loading settings:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!settings) return;

        try {
            setIsSaving(true);
            setError(null);
            setSuccessMessage(null);

            const updateRequest: UpdateLicenceCheckSettingsRequest = {
                enabled: settings.enabled,
                defaultFrequencyMonths: settings.defaultFrequencyMonths,
                preExpiryCheckEnabled: settings.preExpiryCheckEnabled,
                preExpiryCheckDays: settings.preExpiryCheckDays,
                highRiskFrequencyMonths: settings.highRiskFrequencyMonths,
                mediumRiskFrequencyMonths: settings.mediumRiskFrequencyMonths,
                lowRiskFrequencyMonths: settings.lowRiskFrequencyMonths,
                preferredCheckTime: settings.preferredCheckTime,
                timezone: 'UTC', // Always use UTC
                // Batch settings are calculated automatically by backend
                immediateAlertsEnabled: settings.immediateAlertsEnabled,
                dailyDigestEnabled: settings.dailyDigestEnabled,
                dailyDigestTime: settings.dailyDigestTime,
                weeklySummaryEnabled: settings.weeklySummaryEnabled,
                weeklySummaryDay: settings.weeklySummaryDay,
                notificationEmailRecipients: settings.notificationEmailRecipients || [],
                // Consent-triggered check settings
                autoCheckOnConsent: settings.autoCheckOnConsent,
                defaultIncludeCPC: settings.defaultIncludeCPC,
                defaultIncludeTacho: settings.defaultIncludeTacho
            };

            // Store pending settings
            setPendingSettings(updateRequest);

            // Fetch coverage summary to show in confirmation (only if automation is enabled)
            if (settings.enabled) {
                const coverage = await apiClient.testAutomationScheduler();
                setTestResult(coverage);
            }
            setShowConfirmation(true);

        } catch (err: any) {
            setError(err.message || 'Failed to load coverage summary');
        } finally {
            setIsSaving(false);
        }
    };

    const confirmSave = async () => {
        if (!pendingSettings) return;

        try {
            setIsSaving(true);
            setError(null);

            const updated = await apiClient.updateAutomationSettings(pendingSettings);
            setSettings(updated);
            setOriginalSettings(updated);
            onSettingsChange?.(updated);
            setSuccessMessage('Automation settings saved successfully');
            setShowConfirmation(false);
            setPendingSettings(null);
            setTestResult(null);

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to save automation settings');
        } finally {
            setIsSaving(false);
        }
    };

    const cancelSave = () => {
        setShowConfirmation(false);
        setPendingSettings(null);
        setTestResult(null);
    };


    const handleRunNow = () => {
        setShowRunNowConfirmation(true);
    };

    const confirmRunNow = async () => {
        try {
            setIsRunning(true);
            setError(null);
            setShowRunNowConfirmation(false);

            const result = await apiClient.runAutomationNow();
            setSuccessMessage(`Automated check initiated for ${result.driversQueued} drivers`);

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
        } catch (err: any) {
            setError(err.message || 'Failed to run automated check');
        } finally {
            setIsRunning(false);
        }
    };

    const cancelRunNow = () => {
        setShowRunNowConfirmation(false);
    };

    const addEmail = () => {
        if (!settings) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailInput)) {
            setError('Please enter a valid email address');
            return;
        }

        const recipients = settings.notificationEmailRecipients || [];
        if (recipients.includes(emailInput)) {
            setError('Email already in the list');
            return;
        }

        setSettings({
            ...settings,
            notificationEmailRecipients: [...recipients, emailInput]
        });
        setEmailInput('');
        setError(null);
    };

    const removeEmail = (email: string) => {
        if (!settings) return;

        const recipients = settings.notificationEmailRecipients || [];
        setSettings({
            ...settings,
            notificationEmailRecipients: recipients.filter(e => e !== email)
        });
    };

    const hasChanges = (): boolean => {
        if (!settings || !originalSettings) return false;
        return JSON.stringify(settings) !== JSON.stringify(originalSettings);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-700">Failed to load automation settings</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F5E9' }}>
                                    <Settings className="w-6 h-6" style={{ color: '#69BE28' }} />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Automated Licence Checking</h1>
                                    <p className="text-gray-600">Configure automatic DVLA licence verification for your drivers</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={handleRunNow}
                                disabled={isRunning || !settings || !settings.enabled || isDemoOrg}
                                className="px-4 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                {isRunning ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Play className="w-4 h-4" />
                                )}
                                <span>Run Now</span>
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving || !settings || !hasChanges() || isDemoOrg}
                                className="px-4 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                {isSaving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                <span>Save Changes</span>
                            </button>
                        </div>
                    </div>

                    {/* Status Bar */}
                    {settings && (
                        <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: settings.enabled ? '#E8F5E9' : '#FFF3E0' }}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    {settings.enabled ? (
                                        <CheckCircle className="w-5 h-5" style={{ color: '#69BE28' }} />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-orange-500" />
                                    )}
                                    <div>
                                        <p className="font-medium" style={{ color: settings.enabled ? '#69BE28' : '#ED6C02' }}>
                                            {settings.enabled ? 'Automation Enabled' : 'Automation Disabled'}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            {settings.enabled && settings.nextScheduledRun
                                                ? `Next run: ${new Date(settings.nextScheduledRun).toLocaleString()}`
                                                : 'Enable automation to start automatic licence checks'}
                                        </p>
                                    </div>
                                </div>
                                {settings.lastRunDate && (
                                    <div className="text-right text-sm text-gray-600">
                                        <p>Last run: {new Date(settings.lastRunDate).toLocaleString()}</p>
                                        <p>Total checks performed: {settings.totalChecksPerformed}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    {error && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    {successMessage && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <p className="text-green-700">{successMessage}</p>
                        </div>
                    )}
                </div>

                {/* Demo Organization Banner */}
                {isDemoOrg && (
                    <div className="max-w-6xl mx-auto mb-6">
                        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 flex items-start space-x-3">
                            <div className="flex-shrink-0">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-sm font-semibold text-blue-900 mb-1">Demo Mode - View Only</h3>
                                <p className="text-sm text-blue-800">
                                    You are viewing the automation settings in demo mode. These settings are displayed for preview purposes only and cannot be modified.
                                    Please contact Sales if you are interested in this feature.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Save Confirmation Modal */}
                {showConfirmation && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold text-gray-900">Review Your Automation Settings</h2>
                                    <button
                                        onClick={cancelSave}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Cancel"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-gray-600 mb-6">
                                    Please review how your {settings?.enabled ? 'automation' : ''} settings will affect driver licence checks before confirming.
                                </p>

                        {/* Only show detailed coverage when automation is enabled and we have test results */}
                        {settings?.enabled && testResult && (
                            <>
                                {/* Driver Counts */}
                                <div className="grid grid-cols-3 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600 mb-1">Total Drivers</p>
                                <p className="text-2xl font-bold text-gray-900">{testResult.totalDrivers}</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <p className="text-sm text-green-700 mb-1 font-medium">✓ Will Be Checked</p>
                                <p className="text-2xl font-bold text-green-700">{testResult.eligible.count}</p>
                                <p className="text-xs text-green-600 mt-1">Active with consent</p>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <p className="text-sm text-red-700 mb-1 font-medium">✗ Excluded</p>
                                <p className="text-2xl font-bold text-red-700">{testResult.excluded.noConsent + testResult.excluded.inactive}</p>
                                <p className="text-xs text-red-600 mt-1">
                                    {testResult.excluded.noConsent > 0 && `${testResult.excluded.noConsent} no consent`}
                                    {testResult.excluded.noConsent > 0 && testResult.excluded.inactive > 0 && ', '}
                                    {testResult.excluded.inactive > 0 && `${testResult.excluded.inactive} inactive`}
                                </p>
                            </div>
                        </div>

                        {/* Risk Level Breakdown */}
                        {testResult.eligible.count > 0 && settings && (
                            <div className="mb-6">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Eligible Drivers by Risk Level</h3>
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                        <p className="text-xs text-red-700 font-medium">High Risk</p>
                                        <p className="text-xl font-bold text-red-700">{testResult.eligible.riskBreakdown.high}</p>
                                        <p className="text-xs text-red-600 mt-1">Every {settings.highRiskFrequencyMonths} month{settings.highRiskFrequencyMonths > 1 ? 's' : ''}</p>
                                    </div>
                                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                        <p className="text-xs text-yellow-700 font-medium">Medium Risk</p>
                                        <p className="text-xl font-bold text-yellow-700">{testResult.eligible.riskBreakdown.medium}</p>
                                        <p className="text-xs text-yellow-600 mt-1">Every {settings.mediumRiskFrequencyMonths} month{settings.mediumRiskFrequencyMonths > 1 ? 's' : ''}</p>
                                    </div>
                                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                        <p className="text-xs text-green-700 font-medium">Low Risk</p>
                                        <p className="text-xl font-bold text-green-700">{testResult.eligible.riskBreakdown.low}</p>
                                        <p className="text-xs text-green-600 mt-1">Every {settings.lowRiskFrequencyMonths} month{settings.lowRiskFrequencyMonths > 1 ? 's' : ''}</p>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                        <p className="text-xs text-gray-700 font-medium">Unknown Risk</p>
                                        <p className="text-xl font-bold text-gray-700">{testResult.eligible.riskBreakdown.unknown}</p>
                                        <p className="text-xs text-gray-600 mt-1">Every {settings.defaultFrequencyMonths} month{settings.defaultFrequencyMonths > 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                                {/* Next Scheduled Run */}
                                {testResult?.settings?.nextScheduledRun && (
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-blue-700" />
                                            <p className="text-sm text-blue-700 font-medium">Preferred Check Time:</p>
                                            <p className="text-sm text-blue-900 font-semibold">
                                                {settings?.preferredCheckTime} UTC
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Automation Status */}
                        <div className={`p-4 rounded-lg border ${settings?.enabled ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                            <p className={`text-sm ${settings?.enabled ? 'text-green-700' : 'text-yellow-700'}`}>
                                {settings?.enabled ? (
                                    `✓ Automation is enabled. Checks will run daily at ${settings.preferredCheckTime} UTC based on the frequencies above.`
                                ) : (
                                    '⚠️ Automation is currently disabled. Enable automation to start automated licence checks.'
                                )}
                            </p>
                        </div>

                        {/* Consent-Triggered Check Status */}
                        {settings && (
                            <div className={`p-4 rounded-lg border mt-4 ${settings.autoCheckOnConsent ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                                <div className="flex items-start gap-2">
                                    {settings.autoCheckOnConsent ? (
                                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <X className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                    )}
                                    <div>
                                        <p className={`text-sm font-medium ${settings.autoCheckOnConsent ? 'text-blue-900' : 'text-gray-700'}`}>
                                            Consent-Triggered Checks: {settings.autoCheckOnConsent ? 'Enabled' : 'Disabled'}
                                        </p>
                                        <p className={`text-xs mt-1 ${settings.autoCheckOnConsent ? 'text-blue-700' : 'text-gray-600'}`}>
                                            {settings.autoCheckOnConsent ? (
                                                <>
                                                    Drivers will be automatically checked when they approve their consent form.
                                                    {settings.defaultIncludeCPC && ' CPC checks included.'}
                                                    {settings.defaultIncludeTacho && ' Tachograph checks included.'}
                                                </>
                                            ) : (
                                                'Drivers will only be checked during scheduled automation runs or manual checks.'
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Confirmation Buttons */}
                        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                            <button
                                onClick={cancelSave}
                                disabled={isSaving}
                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmSave}
                                disabled={isSaving}
                                className="px-6 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Confirm & Save</span>
                                    </>
                                )}
                            </button>
                        </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Run Now Confirmation Modal */}
                {showRunNowConfirmation && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <Play className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Run Automated Checks Now?</h2>
                                        <p className="text-sm text-gray-600">This will bypass scheduled automation</p>
                                    </div>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
                                    <p className="text-sm text-blue-900">
                                        This will immediately check all active drivers with consent, regardless of when they were last checked.
                                    </p>
                                </div>

                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={cancelRunNow}
                                        disabled={isRunning}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmRunNow}
                                        disabled={isRunning}
                                        className="px-4 py-2 rounded-lg text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                        style={{ backgroundColor: '#69BE28' }}
                                    >
                                        {isRunning ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>Running...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-4 h-4" />
                                                <span>Run Now</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                        {/* General Settings */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center space-x-2 mb-4">
                                <Settings className="w-5 h-5" style={{ color: '#69BE28' }} />
                                <h2 className="text-lg font-semibold text-gray-900">Enable Automation</h2>
                            </div>

                            <div>
                                <label className={`flex items-center space-x-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        checked={settings.enabled}
                                        onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                                        disabled={isDemoOrg}
                                        className="w-5 h-5 rounded"
                                        style={{ accentColor: '#69BE28' }}
                                    />
                                    <div>
                                        <span className="text-sm font-medium text-gray-900">Enable Automated Licence Checking</span>
                                        <p className="text-xs text-gray-600">Automatically check driver licences based on risk levels and schedules below</p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Show message when automation is disabled */}
                        {!settings.enabled && (
                            <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-center text-center py-8">
                                    <div>
                                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">Automation Disabled</h3>
                                        <p className="text-sm text-gray-600 mb-4">
                                            Enable automation above to configure scheduled licence checks based on risk levels and frequencies.
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            Consent-triggered checks can still be enabled independently.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Only show detailed automation settings when automation is enabled */}
                        {settings.enabled && (
                            <>
                                {/* Risk-Based Frequencies - PRIMARY SYSTEM */}
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center space-x-2 mb-2">
                                <Shield className="w-5 h-5" style={{ color: '#69BE28' }} />
                                <h2 className="text-lg font-semibold text-gray-900">Check Frequencies by Risk Level</h2>
                            </div>
                            <p className="text-sm text-gray-600 mb-4">
                                Drivers are automatically checked based on their risk level (determined by penalty points and endorsements)
                            </p>

                            <div className="space-y-4">
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <label className="block text-sm font-medium text-red-900">
                                            High Risk Drivers (score {mediumHighBoundary}+)
                                        </label>
                                    </div>
                                    <select
                                        value={settings.highRiskFrequencyMonths}
                                        onChange={(e) => setSettings({ ...settings, highRiskFrequencyMonths: parseInt(e.target.value) })}
                                        disabled={isDemoOrg}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        <option value={1}>Every month</option>
                                        <option value={3}>Every 3 months</option>
                                        <option value={6}>Every 6 months</option>
                                        <option value={12}>Every 12 months</option>
                                    </select>
                                    <p className="text-xs text-red-700 mt-1">Recommended: Monthly checks for high-risk drivers</p>
                                </div>

                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <label className="block text-sm font-medium text-yellow-900">
                                            Medium Risk Drivers (score {lowMediumBoundary}-{mediumHighBoundary - 1})
                                        </label>
                                    </div>
                                    <select
                                        value={settings.mediumRiskFrequencyMonths}
                                        onChange={(e) => setSettings({ ...settings, mediumRiskFrequencyMonths: parseInt(e.target.value) })}
                                        disabled={isDemoOrg}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        <option value={1}>Every month</option>
                                        <option value={3}>Every 3 months</option>
                                        <option value={6}>Every 6 months</option>
                                        <option value={12}>Every 12 months</option>
                                    </select>
                                    <p className="text-xs text-yellow-700 mt-1">Recommended: Quarterly checks for medium-risk drivers</p>
                                </div>

                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        <label className="block text-sm font-medium text-green-900">
                                            Low Risk Drivers (score &lt;{lowMediumBoundary})
                                        </label>
                                    </div>
                                    <select
                                        value={settings.lowRiskFrequencyMonths}
                                        onChange={(e) => setSettings({ ...settings, lowRiskFrequencyMonths: parseInt(e.target.value) })}
                                        disabled={isDemoOrg}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        <option value={1}>Every month</option>
                                        <option value={3}>Every 3 months</option>
                                        <option value={6}>Every 6 months</option>
                                        <option value={12}>Every 12 months</option>
                                    </select>
                                    <p className="text-xs text-green-700 mt-1">Recommended: Annual checks for low-risk drivers</p>
                                </div>
                            </div>
                        </div>

                        {/* Additional Check Rules */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center space-x-2 mb-2">
                                <AlertCircle className="w-5 h-5" style={{ color: '#69BE28' }} />
                                <h2 className="text-lg font-semibold text-gray-900">Additional Check Rules</h2>
                            </div>
                            <p className="text-sm text-gray-600 mb-4">
                                Special cases and safety checks that run alongside risk-based checking
                            </p>

                            <div className="space-y-4">
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        New & Unassessed Drivers
                                    </label>
                                    <select
                                        value={settings.defaultFrequencyMonths}
                                        onChange={(e) => setSettings({ ...settings, defaultFrequencyMonths: parseInt(e.target.value) })}
                                        disabled={isDemoOrg}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        <option value={1}>Every month</option>
                                        <option value={3}>Every 3 months</option>
                                        <option value={6}>Every 6 months</option>
                                        <option value={12}>Every 12 months</option>
                                    </select>
                                    <p className="text-xs text-gray-600 mt-1">Used for drivers who haven't been checked yet and don't have a risk level assigned</p>
                                </div>

                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <label className={`flex items-center space-x-3 mb-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            checked={settings.preExpiryCheckEnabled}
                                            onChange={(e) => setSettings({ ...settings, preExpiryCheckEnabled: e.target.checked })}
                                            disabled={isDemoOrg}
                                            className="w-5 h-5 rounded"
                                            style={{ accentColor: '#69BE28' }}
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">Pre-Expiry Safety Checks</span>
                                            <p className="text-xs text-gray-600">Automatically check all drivers before their licence expires (regardless of risk level)</p>
                                        </div>
                                    </label>

                                    {settings.preExpiryCheckEnabled && (
                                        <div className="ml-8 mt-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Days Before Expiry
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max="365"
                                                value={settings.preExpiryCheckDays}
                                                onChange={(e) => setSettings({ ...settings, preExpiryCheckDays: parseInt(e.target.value) })}
                                                disabled={isDemoOrg}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                            />
                                            <p className="text-xs text-gray-600 mt-1">Check driver's licence this many days before it expires</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                            </>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                        {/* Consent-Triggered Checks - Always show as it's independent */}
                        <div
                            id="consent-check"
                            className="bg-white rounded-lg shadow-sm p-6 transition-all duration-700"
                            style={highlightConsentSection
                                ? { border: '2px solid #69BE28', boxShadow: '0 0 0 4px rgba(105, 190, 40, 0.15)' }
                                : { border: '1px solid #e5e7eb' }
                            }
                        >
                            <div className="flex items-center space-x-2 mb-2">
                                <CheckCircle className="w-5 h-5" style={{ color: '#69BE28' }} />
                                <h2 className="text-lg font-semibold text-gray-900">Consent-Triggered Checks</h2>
                            </div>
                            <p className="text-sm text-gray-600 mb-4">
                                Automatically trigger a licence check immediately when a driver approves their consent form (independent of scheduled automation)
                            </p>

                            {!user?.organisation?.automatedChecks ? (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-sm text-yellow-800">
                                        ⚠️ This feature requires the Automated Checks add-on. Contact support to enable this feature.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <label className={`flex items-center space-x-3 mb-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                            <input
                                                type="checkbox"
                                                checked={settings.autoCheckOnConsent ?? true}
                                                onChange={(e) => setSettings({ ...settings, autoCheckOnConsent: e.target.checked })}
                                                disabled={isDemoOrg}
                                                className="w-5 h-5 rounded"
                                                style={{ accentColor: '#69BE28' }}
                                            />
                                            <div>
                                                <span className="text-sm font-medium text-gray-900">Automatically check licence when consent is approved</span>
                                                <p className="text-xs text-gray-600">When enabled, drivers will have their licence automatically checked immediately after they sign their consent form. No manual trigger needed.</p>
                                            </div>
                                        </label>

                                        {settings.autoCheckOnConsent && (
                                            <div className="ml-8 space-y-3 pt-2 border-t border-blue-200">
                                                <p className="text-xs text-blue-800 font-medium">Default check options:</p>

                                                <label className={`flex items-center space-x-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={settings.defaultIncludeCPC ?? false}
                                                        onChange={(e) => setSettings({ ...settings, defaultIncludeCPC: e.target.checked })}
                                                        disabled={isDemoOrg}
                                                        className="w-4 h-4 rounded"
                                                        style={{ accentColor: '#69BE28' }}
                                                    />
                                                    <span className="text-xs text-gray-700">Include CPC (Driver Certificate of Professional Competence) checks</span>
                                                </label>

                                                <label className={`flex items-center space-x-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={settings.defaultIncludeTacho ?? false}
                                                        onChange={(e) => setSettings({ ...settings, defaultIncludeTacho: e.target.checked })}
                                                        disabled={isDemoOrg}
                                                        className="w-4 h-4 rounded"
                                                        style={{ accentColor: '#69BE28' }}
                                                    />
                                                    <span className="text-xs text-gray-700">Include Tachograph card checks</span>
                                                </label>
                                            </div>
                                        )}
                                    </div>

                                    {settings.autoCheckOnConsent && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                            <p className="text-xs text-green-800">
                                                ✓ When a driver signs their consent form, a licence check will be automatically queued and completed within seconds. Admin will receive a notification when the check is complete.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Show placeholder when automation is disabled */}
                        {!settings.enabled && (
                            <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-center text-center py-8">
                                    <div>
                                        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">Scheduling & Notifications</h3>
                                        <p className="text-sm text-gray-600">
                                            Enable automation to configure scheduling and notification settings.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Only show right column settings when automation is enabled */}
                        {settings.enabled && (
                            <>
                                {/* Scheduling */}
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center space-x-2 mb-4">
                                <Clock className="w-5 h-5" style={{ color: '#69BE28' }} />
                                <h2 className="text-lg font-semibold text-gray-900">Scheduling</h2>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Preferred Check Time (UTC, 24h format)
                                </label>
                                <input
                                    type="time"
                                    step="1"
                                    value={settings.preferredCheckTime}
                                    onChange={(e) => setSettings({ ...settings, preferredCheckTime: e.target.value })}
                                    disabled={isDemoOrg}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                />
                                <p className="text-xs text-gray-600 mt-1">Time of day to run automated checks (all times in UTC)</p>
                            </div>
                        </div>
                        {/* Notifications */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center space-x-2 mb-4">
                                <Bell className="w-5 h-5" style={{ color: '#69BE28' }} />
                                <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className={`flex items-center space-x-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            checked={settings.immediateAlertsEnabled}
                                            onChange={(e) => setSettings({ ...settings, immediateAlertsEnabled: e.target.checked })}
                                            disabled={isDemoOrg}
                                            className="w-5 h-5 rounded"
                                            style={{ accentColor: '#69BE28' }}
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">Immediate Alerts</span>
                                            <p className="text-xs text-gray-600">Send alerts for high-risk findings immediately</p>
                                        </div>
                                    </label>
                                </div>

                                <div>
                                    <label className={`flex items-center space-x-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            checked={settings.dailyDigestEnabled}
                                            onChange={(e) => setSettings({ ...settings, dailyDigestEnabled: e.target.checked })}
                                            disabled={isDemoOrg}
                                            className="w-5 h-5 rounded"
                                            style={{ accentColor: '#69BE28' }}
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">Daily Digest</span>
                                            <p className="text-xs text-gray-600">Daily summary of automated checks</p>
                                        </div>
                                    </label>
                                </div>

                                {settings.dailyDigestEnabled && (
                                    <div className="ml-8">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Digest Time (24h format)
                                        </label>
                                        <input
                                            type="time"
                                            step="1"
                                            value={settings.dailyDigestTime}
                                            onChange={(e) => setSettings({ ...settings, dailyDigestTime: e.target.value })}
                                            disabled={isDemoOrg}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className={`flex items-center space-x-3 ${isDemoOrg ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            checked={settings.weeklySummaryEnabled}
                                            onChange={(e) => setSettings({ ...settings, weeklySummaryEnabled: e.target.checked })}
                                            disabled={isDemoOrg}
                                            className="w-5 h-5 rounded"
                                            style={{ accentColor: '#69BE28' }}
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-900">Weekly Summary</span>
                                            <p className="text-xs text-gray-600">Weekly trends and statistics</p>
                                        </div>
                                    </label>
                                </div>

                                {settings.weeklySummaryEnabled && (
                                    <div className="ml-8">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Summary Day
                                        </label>
                                        <select
                                            value={settings.weeklySummaryDay}
                                            onChange={(e) => setSettings({ ...settings, weeklySummaryDay: parseInt(e.target.value) })}
                                            disabled={isDemoOrg}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            <option value={0}>Sunday</option>
                                            <option value={1}>Monday</option>
                                            <option value={2}>Tuesday</option>
                                            <option value={3}>Wednesday</option>
                                            <option value={4}>Thursday</option>
                                            <option value={5}>Friday</option>
                                            <option value={6}>Saturday</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Email Recipients */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center space-x-2 mb-4">
                                <Mail className="w-5 h-5" style={{ color: '#69BE28' }} />
                                <h2 className="text-lg font-semibold text-gray-900">Notification Recipients</h2>
                            </div>

                            <div className="space-y-3">
                                <div className="flex space-x-2">
                                    <input
                                        type="email"
                                        value={emailInput}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && !isDemoOrg && addEmail()}
                                        placeholder="email@example.com"
                                        disabled={isDemoOrg}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                    />
                                    <button
                                        onClick={addEmail}
                                        disabled={isDemoOrg}
                                        className="px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: '#69BE28' }}
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {(settings.notificationEmailRecipients || []).map((email, index) => (
                                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                            <span className="text-sm text-gray-700">{email}</span>
                                            <button
                                                onClick={() => removeEmail(email)}
                                                disabled={isDemoOrg}
                                                className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    {(!settings.notificationEmailRecipients || settings.notificationEmailRecipients.length === 0) && (
                                        <p className="text-sm text-gray-500 text-center py-4">No email recipients added</p>
                                    )}
                                </div>
                            </div>
                        </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AutomationSettings;
